import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type UserRole = 'president' | 'manager' | 'agent';

interface TestingConfig {
  enabled: boolean;
  leadMultiplier: number;
  totalLeads: number;
  performanceMode: boolean;
  showTestBadges: boolean;
  autoGenerateEvents: boolean;
  debugMode: boolean;
  userRole: UserRole;
  owlyMode: boolean; // Toggle to show OWLY users instead of company users
}

interface TestingContextType {
  config: TestingConfig;
  updateConfig: (updates: Partial<TestingConfig>) => void;
  resetConfig: () => void;
  getTotalLeads: () => number;
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  owlyMode: boolean; // Determinado automáticamente por el usuario autenticado
}

const defaultConfig: TestingConfig = {
  enabled: true, // Activado para usar mockLeads con datos reales de proyectos
  leadMultiplier: 1,
  totalLeads: 6, // 6 mockLeads con datos reales de proyectos
  performanceMode: false,
  showTestBadges: true,
  autoGenerateEvents: true,
  debugMode: false,
  userRole: 'manager', // Default role
  owlyMode: false // Default: show company users
};

const TestingContext = createContext<TestingContextType | undefined>(undefined);

interface TestingProviderProps {
  children: ReactNode;
}

export const TestingProvider: React.FC<TestingProviderProps> = ({ children }) => {
  const auth = useAuth();
  
  const [config, setConfig] = useState<TestingConfig>(() => {
    // Cargar configuración desde localStorage si existe
    const saved = localStorage.getItem('owly-testing-config');
    if (saved) {
      try {
        return { ...defaultConfig, ...JSON.parse(saved) };
      } catch (error) {
      }
    }
    return defaultConfig;
  });

  // Determinar automáticamente owlyMode basado en el usuario autenticado
  // Si es superuser o no tiene company_data → OWLY Mode (Portal de administración)
  // Si tiene company_data → Company Mode (Portal CRM del cliente)
  const owlyMode = React.useMemo(() => {
    if (!auth.user) {
      return false;
    }

    // Superusers siempre ven el portal de OWLY
    if (auth.user.is_superuser) {
      return true;
    }

    // Brokers/Agencies are platform users (no company_data) but they work
    // INSIDE a tenant company's portal — never treat them as OWLY internal.
    if (auth.user.role === 'owly_broker' || auth.user.role === 'owly_agency') {
      return false;
    }

    // Si no tiene company_data asignado, es usuario interno de OWLY
    if (!auth.user.company_data && !auth.user.company) {
      return true;
    }

    // Si tiene company_data, es usuario de una compañía cliente
    return false;
  }, [auth.user]);

  // Guardar en localStorage cuando cambie la configuración
  useEffect(() => {
    localStorage.setItem('owly-testing-config', JSON.stringify(config));
  }, [config]);

  const updateConfig = (updates: Partial<TestingConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const resetConfig = () => {
    setConfig(defaultConfig);
    localStorage.removeItem('owly-testing-config');
  };

  const getTotalLeads = () => {
    return config.totalLeads * config.leadMultiplier;
  };

  const setUserRole = (role: UserRole) => {
    updateConfig({ userRole: role });
  };

  const value: TestingContextType = {
    config,
    updateConfig,
    resetConfig,
    getTotalLeads,
    userRole: config.userRole,
    setUserRole,
    owlyMode, // Determinado automáticamente
  };

  return (
    <TestingContext.Provider value={value}>
      {children}
    </TestingContext.Provider>
  );
};

export const useTesting = (): TestingContextType => {
  const context = useContext(TestingContext);
  if (!context) {
    throw new Error('useTesting must be used within a TestingProvider');
  }
  return context;
};

// Hook para determinar si estamos en modo testing
export const useIsTestingMode = (): boolean => {
  const { config } = useTesting();
  return config.enabled && process.env.NODE_ENV === 'development';
};